import { Link } from '@inertiajs/react'
import { DashboardLayout } from '@/components/layout/DashboardLayout'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { useBasePath } from '@/lib/paths'
import { ArrowLeft } from 'lucide-react'

type Filing = {
  id: string
  type_name: string
}

type Record = {
  id: string
  period_start_date: string
  period_end_date: string
}

type FilingDetail = {
  id: string
  reference_number: string | null
  submitted_at: string | null
  accepted: boolean
  rejected: boolean
  pending: boolean
  rejection_reason: string | null
} | null

type Props = {
  filing: Filing
  record: Record
  filing_detail: FilingDetail
}

export default function ComplianceFilingRecordsFilingsShow({ filing, record, filing_detail }: Props) {
  const basePath = useBasePath()

  return (
    <DashboardLayout
      title="Filing Status"
      breadcrumbs={[
        { label: 'Compliance Filings', href: 'compliance_filings' },
        { label: filing.type_name, href: `compliance_filings/${filing.id}` },
        { label: `${record.period_start_date} - ${record.period_end_date}`, href: `compliance_filings/${filing.id}/records/${record.id}` },
        { label: 'Filing' },
      ]}
    >
      <div>
        <div className="mb-8">
          <h1 className="text-xl font-semibold tracking-tight">Filing Status</h1>
          <p className="text-sm text-muted-foreground">{record.period_start_date} — {record.period_end_date}</p>
        </div>

        <div className="space-y-6">
          <section className="border rounded-lg">
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <h2 className="text-sm font-medium text-foreground">Government Filing</h2>
              {filing_detail && (
                <div className="flex items-center gap-2">
                  {filing_detail.accepted && <Badge className="bg-green-600 text-white font-normal text-xs">Accepted</Badge>}
                  {filing_detail.rejected && <Badge className="bg-red-600 text-white font-normal text-xs">Rejected</Badge>}
                  {filing_detail.pending && <Badge className="bg-blue-600 text-white font-normal text-xs">Pending</Badge>}
                </div>
              )}
            </div>
            <div className="px-4 py-3">
              {filing_detail ? (
                <div className="space-y-1.5">
                  {filing_detail.reference_number && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Reference</span>
                      <span>{filing_detail.reference_number}</span>
                    </div>
                  )}
                  {filing_detail.submitted_at && (
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Submitted</span>
                      <span>{new Date(filing_detail.submitted_at).toLocaleDateString()}</span>
                    </div>
                  )}
                  {filing_detail.rejection_reason && (
                    <div className="mt-2 text-sm text-red-600 bg-red-50 p-2 rounded">
                      {filing_detail.rejection_reason}
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">No filing submitted yet</p>
              )}
            </div>
          </section>

          <div>
            <Button variant="outline" asChild>
              <Link href={`${basePath}/compliance_filings/${filing.id}/records/${record.id}`}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Record
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  )
}
